import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { getAbandenedBookingsFilter, downloadAbandonedCartsExcel } from "../../../actions/admin/booking";
import { setAlert } from "../../../actions/alert";
import Alert from "../../layout/Alert";
import { DownloadIcon } from "../../icons";
import BookingItem from "../booking/BookingItem";
import AdminPage from "../_components/AdminPage";
import AdminTable from "../_components/AdminTable";
import AdminFilterBar, { DateRangeField } from "../_components/AdminFilterBar";

const TABLE_COLUMNS = [
  { key: "no", title: "#", width: "tw:w-[60px] tw:rounded-tl-[5px]", skeleton: "narrow" },
  { key: "name", title: "Customer Name", width: "tw:w-[200px]" },
  { key: "userType", title: "User Type", width: "tw:w-[140px]", skeleton: "stacked" },
  { key: "status", title: "Booking Status", width: "tw:w-[190px]", skeleton: "badge" },
  { key: "bookingType", title: "Transfer Type", width: "tw:w-[200px]" },
  { key: "payment", title: "Payment Status", width: "tw:w-[200px]", skeleton: "badge" },
  { key: "createdOn", title: "Created On", width: "tw:w-[170px]" },
  { key: "pickup", title: "Pick-up Time", width: "tw:w-[170px]" },
  { key: "pickupLocation", title: "Pick-up Location", width: "tw:w-[260px]" },
  { key: "referrer", title: "Referrer", width: "tw:w-[140px]" },
  { key: "action", title: "Action", width: "tw:w-[120px] tw:rounded-tr-[5px]", sticky: true, align: "center" },
];

// Not the old "abstartDate"/"abendDate": those hold pick-up dates in the future,
// left over from when this screen filtered on travel date, and reading them back
// against an abandonment date would show every admin an empty list.
const STORAGE_KEYS = {
  startDate: "abandonedCartStartDate",
  endDate: "abandonedCartEndDate",
};

const DEFAULT_RANGE_DAYS = 30;

const getDefaultDateRange = () => ({
  startDate: moment().subtract(DEFAULT_RANGE_DAYS, "days"),
  endDate: moment(),
});

const getStoredDateRange = () => {
  const stored = {
    startDate: localStorage.getItem(STORAGE_KEYS.startDate),
    endDate: localStorage.getItem(STORAGE_KEYS.endDate),
  };
  const fallback = getDefaultDateRange();

  return {
    startDate: stored.startDate !== null ? moment(stored.startDate) : fallback.startDate,
    endDate: stored.endDate !== null ? moment(stored.endDate) : fallback.endDate,
  };
};

const DownloadExcelButton = ({ onClick, isDownloading, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || isDownloading}
    className="tw:flex tw:h-[45px] tw:items-center tw:gap-2 tw:!rounded-[5px] tw:border-0 tw:bg-[#282828] tw:px-[17px] tw:text-[15px] tw:leading-[22px] tw:!text-white tw:font-hanken-grotesk tw:disabled:cursor-not-allowed tw:disabled:opacity-50"
  >
    <DownloadIcon color="#FFFFFF" size={16} />
    {isDownloading ? "Generating..." : "Download Excel"}
  </button>
);

const AbandentCartList = () => {
  const [dateRange, setdateRange] = useState(getStoredDateRange);
  const booking = useSelector((state) => state.booking);
  const { loading, bookings } = booking;
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    dispatch(getAbandenedBookingsFilter(dateRange.startDate, dateRange.endDate, true));
  }, [dispatch, dateRange]);

  const onDateRangeChange = (range) => {
    setdateRange(range);
    localStorage.setItem(STORAGE_KEYS.startDate, moment(range.startDate).toISOString());
    localStorage.setItem(STORAGE_KEYS.endDate, moment(range.endDate).toISOString());
  };

  const onClearFilters = () => {
    localStorage.removeItem(STORAGE_KEYS.startDate);
    localStorage.removeItem(STORAGE_KEYS.endDate);
    setdateRange(getDefaultDateRange());
  };

  const onDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      await downloadAbandonedCartsExcel(dateRange.startDate, dateRange.endDate);
    } catch (err) {
      dispatch(setAlert("Could not generate the Excel file. Please try again.", "danger"));
    } finally {
      setIsDownloading(false);
    }
  };

  const rows = Array.isArray(bookings) ? bookings : bookings?.data || [];

  return (
    <AdminPage title="Abandoned Carts List">
      <Alert />
      <AdminFilterBar
        columns={4}
        defaultOpen
        onClear={onClearFilters}
        trailing={
          <DownloadExcelButton onClick={onDownloadExcel} isDownloading={isDownloading} disabled={loading || !rows.length} />
        }
      >
        <DateRangeField label="Abandoned Date・Period" dateRange={dateRange} onChange={onDateRangeChange} />
      </AdminFilterBar>

      <AdminTable
        columns={TABLE_COLUMNS}
        minWidth={1850}
        loading={loading}
        skeletonRowCount={10}
        isEmpty={!rows.length}
        emptyText="No abandoned carts found."
      >
        {rows.map((item, index) => (
          <BookingItem key={item._id} bookings={item} count={index} />
        ))}
      </AdminTable>
    </AdminPage>
  );
};

export default AbandentCartList;
